// Lazy-load posthog-js only when product analytics is actually enabled.
// Saves ~10kB+ from the initial bundle when no PostHog key is configured.
import { PRODUCT_ANALYTICS_HOST } from './appConfig'

const posthogKey = import.meta.env.VITE_POSTHOG_KEY
const posthogHost = PRODUCT_ANALYTICS_HOST
const e2eMocksEnabled = import.meta.env.VITE_E2E_MOCKS === 'true'

export const isProductAnalyticsEnabled = Boolean(posthogKey) && !e2eMocksEnabled

let posthogInstance = null
let initPromise = null

async function loadPosthog() {
    if (posthogInstance) return posthogInstance
    const mod = await import('posthog-js')
    posthogInstance = mod.default || mod
    return posthogInstance
}

export async function initializeProductAnalytics() {
    if (!isProductAnalyticsEnabled) return
    if (initPromise) return initPromise
    initPromise = (async () => {
        const ph = await loadPosthog()
        if (ph.__loaded) return
        ph.init(posthogKey, {
            api_host: posthogHost,
            capture_pageview: false,
            person_profiles: 'identified_only',
        })
    })()
    return initPromise
}

export function identifyProductUser(user, profile) {
    if (!isProductAnalyticsEnabled || !user?.id) return
    if (posthogInstance) {
        posthogInstance.identify(user.id, {
            email: user.email,
            full_name: profile?.full_name,
            company_id: profile?.company_id,
            role: profile?.role,
        })
    } else {
        loadPosthog().then((ph) => {
            ph.identify(user.id, {
                email: user.email,
                full_name: profile?.full_name,
                company_id: profile?.company_id,
                role: profile?.role,
            })
        }).catch(() => {})
    }
}

export function resetProductAnalytics() {
    if (!isProductAnalyticsEnabled) return
    if (posthogInstance) posthogInstance.reset()
}

export function sanitizeAnalyticsProperties(properties = {}) {
    return Object.fromEntries(
        Object.entries(properties).filter(([, value]) => value !== undefined),
    )
}

export function trackProductEvent(eventName, properties = {}) {
    if (!isProductAnalyticsEnabled) return
    const props = sanitizeAnalyticsProperties(properties)
    if (posthogInstance) {
        posthogInstance.capture(eventName, props)
    } else {
        loadPosthog().then((ph) => ph.capture(eventName, props)).catch(() => {})
    }
}
